import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Piko } from '../components/Piko';
import { ClayButton } from '../components/ClayButton';
import { getState, setState } from '../core/storage';

const STEPS = ['welcome', 'nickname', 'language', 'audio', 'session', 'privacy'];

export default function Onboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [nickname, setNickname] = useState('');
  const [language, setLanguage] = useState('ms');
  const [bilingual, setBilingual] = useState(false);
  const [music, setMusic] = useState(true);
  const [minutes, setMinutes] = useState(10);
  const [missions, setMissions] = useState(true);

  const finish = () => {
    setState({
      onboarded: true,
      profile: { nickname: nickname.trim() || 'Adik', language, bilingual },
      settings: { ...getState().settings, music, sessionMinutes: minutes, missions },
    });
    nav('/home');
  };

  const s = STEPS[step];
  const next = () => (step === STEPS.length - 1 ? finish() : setStep(step + 1));

  return (
    <div className="fixed inset-0 overflow-y-auto" style={{ background: 'linear-gradient(180deg,#FCF9F2,#FFE9C7)' }}>
      <div className="max-w-md mx-auto min-h-full flex flex-col items-center justify-center gap-6 px-6 py-10">
        <Piko pose={s === 'privacy' ? 'think' : 'happy'} size={120} />
        <AnimatePresence mode="wait">
          <motion.div key={s} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
            className="w-full bg-white/85 backdrop-blur-xl rounded-[2rem] p-7 shadow-lg border border-white/60" data-testid={`onboarding-step-${s}`}>
            {s === 'welcome' && (<>
              <h2 className="font-heading text-2xl font-bold text-navy">Selamat datang ke PintarPlay</h2>
              <p className="font-body text-navy/75 mt-3 leading-relaxed">Aplikasi belajar sambil bermain untuk anak berumur 2–3 tahun. Permainan pendek, arahan bersuara, misi pergerakan, dan interaksi ibu bapa — bukan sekadar skrin.</p>
              <p className="font-body text-navy/60 mt-2 text-sm">PintarPlay menyokong pembelajaran awal. Ia tidak mengukur IQ dan tidak menggantikan guru atau doktor.</p>
            </>)}
            {s === 'nickname' && (<>
              <h2 className="font-heading text-2xl font-bold text-navy">Nama panggilan anak</h2>
              <input data-testid="nickname-input" value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="Contoh: Aisyah"
                className="mt-4 w-full h-14 rounded-2xl border-2 border-peach bg-cream px-4 font-body text-lg text-navy outline-none focus:border-sunshine" />
              <p className="font-body text-navy/50 text-sm mt-2">Hanya nama panggilan. Tiada maklumat peribadi lain diperlukan.</p>
            </>)}
            {s === 'language' && (<>
              <h2 className="font-heading text-2xl font-bold text-navy">Bahasa</h2>
              <div className="grid gap-3 mt-4">
                {[['ms', 'Bahasa Melayu'], ['en', 'English']].map(([v, l]) => (
                  <button key={v} data-testid={`lang-${v}`} onClick={() => setLanguage(v)}
                    className={`h-14 rounded-2xl font-body font-bold text-lg ${language === v ? 'bg-sunshine text-navy' : 'bg-cream text-navy/60'}`}>{l}</button>
                ))}
                <button data-testid="bilingual-toggle" onClick={() => setBilingual(!bilingual)}
                  className={`h-14 rounded-2xl font-body font-bold text-lg ${bilingual ? 'bg-leaf text-navy' : 'bg-cream text-navy/60'}`}>
                  Mod Dwibahasa {bilingual ? '✓' : ''}</button>
              </div>
            </>)}
            {s === 'audio' && (<>
              <h2 className="font-heading text-2xl font-bold text-navy">Audio</h2>
              <p className="font-body text-navy/70 mt-2">Suara arahan sentiasa hidup. Muzik latar boleh dimatikan.</p>
              <button data-testid="music-toggle" onClick={() => setMusic(!music)}
                className={`mt-4 h-14 w-full rounded-2xl font-body font-bold text-lg ${music ? 'bg-sky text-white' : 'bg-cream text-navy/60'}`}>
                Muzik: {music ? 'Hidup' : 'Mati'}</button>
            </>)}
            {s === 'session' && (<>
              <h2 className="font-heading text-2xl font-bold text-navy">Tempoh sesi</h2>
              <div className="grid grid-cols-3 gap-3 mt-4">
                {[8, 10, 12].map((m) => (
                  <button key={m} data-testid={`minutes-${m}`} onClick={() => setMinutes(m)}
                    className={`h-14 rounded-2xl font-body font-bold ${minutes === m ? 'bg-sunshine text-navy' : 'bg-cream text-navy/60'}`}>{m} min</button>
                ))}
              </div>
              <button data-testid="missions-toggle" onClick={() => setMissions(!missions)}
                className={`mt-3 h-14 w-full rounded-2xl font-body font-bold ${missions ? 'bg-leaf text-navy' : 'bg-cream text-navy/60'}`}>
                Misi pergerakan & rumah: {missions ? 'Hidup' : 'Mati'}</button>
            </>)}
            {s === 'privacy' && (<>
              <h2 className="font-heading text-2xl font-bold text-navy">Privasi</h2>
              <ul className="font-body text-navy/75 mt-3 space-y-2 text-sm leading-relaxed list-disc pl-5">
                <li>Semua kemajuan anak disimpan dalam pelayar peranti ini sahaja (local-first).</li>
                <li>Tiada log masuk diperlukan untuk bermain. Tiada iklan, tiada penjejakan.</li>
                <li>Kami tidak mengumpul nama penuh, gambar, suara, atau lokasi.</li>
                <li>Jika data pelayar dipadam, kemajuan akan hilang.</li>
              </ul>
            </>)}
            <ClayButton testId="onboarding-next-btn" onClick={next} small className="w-full mt-6" color={s === 'privacy' ? '#4ADE80' : '#FFD166'}>
              {s === 'privacy' ? 'Mula!' : 'Seterusnya'}
            </ClayButton>
          </motion.div>
        </AnimatePresence>
        <div className="flex gap-2">{STEPS.map((_, i) => (
          <div key={i} className={`w-2.5 h-2.5 rounded-full ${i <= step ? 'bg-sunshine' : 'bg-navy/15'}`} />))}
        </div>
      </div>
    </div>
  );
}
